import type { ChangedItem } from './catalogue';

export type Fault =
  | 'slug_missing' | 'slug_malformed' | 'slug_too_long'
  | 'feature_name_blank' | 'confers_nothing' | 'duration_out_of_range' | 'already_exists';

export type Violation = { id: string; fault: Fault };

const SLUG_PATTERN = /^[a-z0-9_-]+$/;
const SLUG_MAX = 100;

// Mirrors backend CatalogueSlug (enterprise-entitlements). Dup check is case-insensitive
// and scoped to one kind — the caller passes only that kind's existing slugs.
export function slugFault(slug: string, existingSlugs: readonly string[]): Fault | null {
  if (slug.length === 0) return 'slug_missing';
  if (slug.length > SLUG_MAX) return 'slug_too_long';
  if (!SLUG_PATTERN.test(slug)) return 'slug_malformed';
  const lower = slug.toLowerCase();
  if (existingSlugs.some((s) => s.toLowerCase() === lower)) return 'already_exists';
  return null;
}

const DURATION_MIN = 1;
const DURATION_MAX = 365;

// Validates ONLY the changed set (diffCatalogue output), never the whole draft — untouched
// legacy items must never block an unrelated publish. Scope is the client-reachable faults;
// availability, requiresTier and confers-references are prevented structurally by the drawers,
// and already_exists is a server 409 left to the rejected channel.
export function validateChanges(changes: readonly ChangedItem[]): Violation[] {
  const out: Violation[] = [];
  for (const c of changes) {
    const features = c.draftBody.features ?? [];
    if (features.some((f) => f.trim().length === 0)) out.push({ id: c.id, fault: 'feature_name_blank' });
    if (c.kind === 'trial-offer') {
      if ((c.draftBody.confers ?? []).length === 0) out.push({ id: c.id, fault: 'confers_nothing' });
      const d = c.draftBody.durationDays;
      if (d == null || d < DURATION_MIN || d > DURATION_MAX) out.push({ id: c.id, fault: 'duration_out_of_range' });
    }
  }
  return out;
}
